import { Box, Button, Card, CardContent, CardHeader, TextField, Typography } from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { type ChangeEvent, useState, type JSX } from "react";

import { api } from "../../../lib/api.js";
import { upstreamErrorMessage } from "../../../lib/upstreamError.js";

/** Minimum new-password length — mirrors portal-api's
 *  `validates :password, length: { minimum: 12 }` so we fail fast in the UI
 *  instead of round-tripping to upstream for the obvious case. */
const MIN_PASSWORD_LENGTH = 12;

/**
 * Change-password card — current + new + confirm, with client-side length +
 * match checks before POSTing `/api/profile/change-password`. Extracted from
 * `ProfilePage.tsx`.
 */
export function ChangePasswordCard(): JSX.Element {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [clientError, setClientError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const change = useMutation({
    mutationFn: async () =>
      api("/api/profile/change-password", {
        method: "POST",
        body: {
          current_password: current,
          new_password: next,
          new_password_confirmation: confirm,
        },
      }),
    onSuccess: () => {
      setDone(true);
      setCurrent("");
      setNext("");
      setConfirm("");
    },
  });

  function edit(setter: (v: string) => void) {
    return (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setter(e.target.value);
      setDone(false);
      setClientError(null);
    };
  }

  function submit(): void {
    setDone(false);
    setClientError(null);
    if (next.length < MIN_PASSWORD_LENGTH) {
      setClientError(`New password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
      return;
    }
    if (next !== confirm) {
      setClientError("New passwords don't match.");
      return;
    }
    change.mutate();
  }

  const canSubmit =
    current.length > 0 && next.length > 0 && confirm.length > 0 && !change.isPending;

  return (
    <Card>
      <CardHeader title="Password" />
      <CardContent>
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
          sx={{ display: "grid", gap: 2 }}
        >
          <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
            Change the password you use to sign in. You'll stay signed in on this
            device.
          </Typography>
          <TextField
            id="profile-current-password"
            type="password"
            label="Current password"
            autoComplete="current-password"
            value={current}
            onChange={edit(setCurrent)}
            size="small"
            fullWidth
          />
          <TextField
            id="profile-new-password"
            type="password"
            label="New password"
            autoComplete="new-password"
            value={next}
            onChange={edit(setNext)}
            size="small"
            fullWidth
            helperText={`At least ${MIN_PASSWORD_LENGTH} characters.`}
          />
          <TextField
            id="profile-confirm-password"
            type="password"
            label="Confirm new password"
            autoComplete="new-password"
            value={confirm}
            onChange={edit(setConfirm)}
            size="small"
            fullWidth
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Button type="submit" variant="contained" disabled={!canSubmit}>
              {change.isPending ? "Changing…" : "Change password"}
            </Button>
            {clientError ? (
              <Typography component="span" sx={{ fontSize: 12, color: "error.main" }}>
                {clientError}
              </Typography>
            ) : change.isError ? (
              <Typography component="span" sx={{ fontSize: 12, color: "error.main" }}>
                {upstreamErrorMessage(change.error, "Could not change your password. Try again.")}
              </Typography>
            ) : (
              done &&
              !change.isPending && (
                <Typography component="span" sx={{ fontSize: 12, color: "success.main" }}>
                  Password changed
                </Typography>
              )
            )}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}
